import { deriveResourceType, type WhatIfChange, type WhatIfPropertyChange } from "./whatif.js";

export interface NoiseRule { resourceType: string; pathPattern: RegExp; reason: string }

// Real, intended changes that an approved apply SHOULD make. Shown, not failed.
export const KNOWN_PENDING: NoiseRule[] = [
  { resourceType: "Microsoft.ServiceBus/namespaces", pathPattern: /(^|\.)minimumTlsVersion$/, reason: "TLS 1.0→1.2 (the #64 fix); apply will enforce it" },
];

// Cosmetic / RP-owned / unanalyzable lines that an apply will NOT meaningfully change.
export const KNOWN_NOISE: NoiseRule[] = [
  { resourceType: "Microsoft.KeyVault/vaults", pathPattern: /(^|\.)networkAcls$/, reason: "declares the effective default (AzureServices/Allow); verified apply-time no-op" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)ingress\.exposedPort$/, reason: "RP runtime field" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)ingress\.traffic$/, reason: "RP runtime field" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)runningStatus$/, reason: "RP runtime status" },
  { resourceType: "Microsoft.App/managedEnvironments", pathPattern: /(^|\.)peerAuthentication$/, reason: "RP default" },
  { resourceType: "Microsoft.App/managedEnvironments", pathPattern: /(^|\.)peerTrafficConfiguration$/, reason: "RP default" },
  // Connections can be account-scoped or project-scoped depending on the deployment's tracing mode.
  // Rules below match both resource type variants to ensure consistent noise classification.
  { resourceType: "Microsoft.CognitiveServices/accounts/connections", pathPattern: /(^|\.)credentials$/, reason: "write-only credential — always re-shows" },
  { resourceType: "Microsoft.CognitiveServices/accounts/connections", pathPattern: /(^|\.)(group|isDefault|peRequirement|peStatus|useWorkspaceManagedIdentity)$/, reason: "RP connection metadata" },
  { resourceType: "Microsoft.CognitiveServices/accounts/projects/connections", pathPattern: /(^|\.)credentials$/, reason: "write-only credential — always re-shows" },
  { resourceType: "Microsoft.CognitiveServices/accounts/projects/connections", pathPattern: /(^|\.)(group|isDefault|peRequirement|peStatus|useWorkspaceManagedIdentity)$/, reason: "RP connection metadata" },
  { resourceType: "Microsoft.Insights/components", pathPattern: /(^|\.)(Flow_Type|Request_Source)$/, reason: "RP-stamped" },
  { resourceType: "Microsoft.ServiceBus/namespaces/queues", pathPattern: /(^|\.)(autoDeleteOnIdle|duplicateDetectionHistoryTimeWindow|maxMessageSizeInKilobytes|maxSizeInMegabytes)$/, reason: "RP default the bicep doesn't declare" },
  // The template regenerates the relay's shared secret on every deployment unless the
  // current value is passed through, so a comparison that omits it reports a fresh
  // difference every run. Ownership of that parameter sits with the voice work, not
  // here — this comparison neither reads it nor asserts on it.
  //
  // The indexed rule is deliberately end-anchored to `.value` ONLY: an unanchored
  // pattern would also swallow `configuration.secrets[N].name`,
  // `.keyVaultUrl`/vault reference, and `.identity` — a repointed vault reference or a
  // swapped backing identity on ANY container app would then be invisible to the very
  // check meant to catch it, not just the regenerated secret's own value.
  //
  // REMOVE THESE TWO RULES when the deployment path stops regenerating the value.
  // Their presence is the marker that it still does.
  // Storage RP defaults the bicep does not declare. what-if renders "the template
  // omits this, so it would be removed", but the RP re-stamps them on every write
  // and an apply is a verified no-op. Narrow on purpose: `deleteRetentionPolicy`
  // and the two encryption-scope fields only. An unanchored `properties` rule on
  // blobServices would swallow a real change to versioning, CORS or public access
  // on the account that holds every conversation.
  { resourceType: "Microsoft.Storage/storageAccounts/blobServices", pathPattern: /(^|\.)properties$/, reason: "RP-owned blob-service defaults (deleteRetentionPolicy); the bicep declares none" },
  { resourceType: "Microsoft.Storage/storageAccounts/blobServices/containers", pathPattern: /(^|\.)(defaultEncryptionScope|denyEncryptionScopeOverride)$/, reason: "RP default ($account-encryption-key); the bicep declares no scope" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)configuration\.secrets$/, reason: "shared relay secret is regenerated on every deployment — owned elsewhere" },
  { resourceType: "Microsoft.App/containerApps", pathPattern: /(^|\.)configuration\.secrets\[\d+\]\.value$/, reason: "shared relay secret is regenerated on every deployment — owned elsewhere" },
];


const PROBE_LEAF = /\.probes\[\d+\]\./;
const PROBE_TYPE_LEAF = /\.probes\[\d+\]\.type$/;

/**
 * True when a resource's probe deltas are a pure REORDERING — the same set of probe
 * types before and after, in a different order.
 *
 * The Container Apps RP stores probes in ITS OWN canonical order, whatever order the
 * template declares. what-if diffs arrays by index, so one reordering renders as a diff
 * on every field of every probe: on m8t's canary that is TEN "unexpected" findings from
 * a deployment that is byte-for-byte correct.
 *
 * PROVEN, not assumed: a forced infra apply of the exact template ran on 2026-08-11
 * (deployment `m8t-converge-915c95929419`, Succeeded), the probe order did not move, no
 * new revision was created, and a re-measurement reported the same ten. No apply can
 * reconcile this, so it is noise by the definition this classifier exists for.
 *
 * Keyed on the TYPE multiset rather than on the path, so it cannot degrade into "ignore
 * probes": a probe added, removed, or retyped changes that multiset and every finding on
 * the resource stays unexpected.
 *
 * KNOWN RESIDUAL GAP, stated rather than hidden: when a reordering coincides with a real
 * field change on one probe (a threshold edit shipped in the same release), this
 * suppresses that too. The delta carries only CHANGED leaves, so the unchanged fields of
 * each probe are absent and the arrays cannot be reconstructed to pair probes by type.
 * Suppressing a co-occurring edit is the price of not reporting ten false findings on
 * every run forever; a probe change shipped on its own — the normal case — is unaffected,
 * because with no reordering there are no `.type` leaves and this returns false.
 */
export function isProbeReordering(leaves: { path: string; before?: unknown; after?: unknown }[]): boolean {
  const types = leaves.filter((l) => PROBE_TYPE_LEAF.test(l.path));
  if (types.length === 0) return false;
  const before = types.map((l) => l.before).filter((v): v is string => typeof v === "string").sort();
  const after = types.map((l) => l.after).filter((v): v is string => typeof v === "string").sort();
  if (before.length !== types.length || after.length !== types.length) return false;
  return before.every((v, i) => v === after[i]);
}

export interface ClassifiedChange {
  resourceType: string; resourceName: string; path: string;
  before?: unknown; after?: unknown; reason?: string;
}
export interface ClassifiedResult { noise: ClassifiedChange[]; pending: ClassifiedChange[]; unexpected: ClassifiedChange[] }

const shortName = (resourceId: string) =>
  resourceId.startsWith("[") ? "(role assignment)" : resourceId.split("/").pop() ?? resourceId;

// An ARM template expression like "[reference(...).x]" / "[format(...)]" — a
// cosmetic what-if artifact that resolves to the same value at deploy time.
function isArmExpression(v: unknown): boolean {
  return typeof v === "string" && /^\[.*\b(reference|format|guid|subscriptionResourceId|extensionResourceId)\(/.test(v) && v.endsWith("]");
}

// Flatten a delta tree into leaf property changes with dotted/indexed paths.
function flattenDelta(delta: WhatIfPropertyChange[], prefix = ""): WhatIfPropertyChange[] {
  const out: WhatIfPropertyChange[] = [];
  for (const d of delta) {
    const segment = /^[0-9]+$/.test(d.path) ? `[${d.path}]` : (prefix ? `.${d.path}` : d.path);
    const path = prefix ? `${prefix}${segment}` : d.path;
    if (d.children && d.children.length > 0) out.push(...flattenDelta(d.children, path));
    else out.push({ ...d, path });
  }
  return out;
}

/** Classify each what-if change into noise / pending / unexpected. Pure. */
export function classifyWhatIf(changes: WhatIfChange[]): ClassifiedResult {
  const noise: ClassifiedChange[] = [], pending: ClassifiedChange[] = [], unexpected: ClassifiedChange[] = [];
  for (const c of changes) {
    if (c.changeType === "NoChange" || c.changeType === "Ignore") continue; // not drift
    if (c.changeType === "Create" || c.changeType === "Delete") {
      // A whole new/removed resource is always signal — never let per-property
      // noise rules swallow it.
      unexpected.push({ resourceType: deriveResourceType(c.resourceId), resourceName: shortName(c.resourceId), path: `(resource ${c.changeType.toLowerCase()})` });
      continue;
    }
    if (c.changeType === "Unsupported") {
      const entry: ClassifiedChange = { resourceType: "(unsupported)", resourceName: shortName(c.resourceId), path: "(whole resource)" };
      if (c.resourceId.includes('Microsoft.Authorization/roleAssignments')) noise.push({ ...entry, reason: "role assignment GUID from reference() — unanalyzable" });
      else unexpected.push(entry);
      continue;
    }
    const resourceType = deriveResourceType(c.resourceId);
    const resourceName = shortName(c.resourceId);
    const leaves = flattenDelta(c.delta ?? []);
    // Per-RESOURCE, before any per-leaf rule: the reordering is a property of the whole
    // probe array, not of any single leaf, so no path pattern can express it.
    const probesReordered = isProbeReordering(leaves);
    for (const leaf of leaves) {
      const base: ClassifiedChange = { resourceType, resourceName, path: leaf.path, before: leaf.before, after: leaf.after };
      if (probesReordered && PROBE_LEAF.test(leaf.path)) {
        noise.push({ ...base, reason: "probe array reordered by the RP; same probe set — no apply can reconcile it" });
        continue;
      }
      if (leaf.propertyChangeType === "NoEffect") { noise.push({ ...base, reason: "NoEffect (no-op)" }); continue; }
      if (isArmExpression(leaf.after)) { noise.push({ ...base, reason: "reference()/format() artifact — same value" }); continue; }
      const pend = KNOWN_PENDING.find((rule) => rule.resourceType === resourceType && rule.pathPattern.test(leaf.path));
      if (pend) { pending.push({ ...base, reason: pend.reason }); continue; }
      const n = KNOWN_NOISE.find((rule) => rule.resourceType === resourceType && rule.pathPattern.test(leaf.path));
      if (n) { noise.push({ ...base, reason: n.reason }); continue; }
      unexpected.push(base);
    }
  }
  return { noise, pending, unexpected };
}
